import React, { useCallback, useMemo } from "react";
import ReactMarkdown from "react-markdown";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";
import { useNavigate } from "react-router-dom";
import { fallbackToApiMedia } from "../utils/imageUtils";

const REMARK_PLUGINS = [remarkBreaks, remarkGfm];

const SimpleMarkdown = ({ content, className = "" }) => {
  const navigate = useNavigate();

  const handleLinkClick = useCallback(
    (e, href) => {
      if (href && href.startsWith("/reefcyclopedia/")) {
        e.preventDefault();
        window.scrollTo(0, 0);
        navigate(href);
      }
    },
    [navigate]
  );

  const components = useMemo(
    () => ({
      h1: ({ children }) => <h1 className="text-lg font-bold mt-3 mb-2">{children}</h1>,
      h2: ({ children }) => <h2 className="text-lg font-bold mt-3 mb-2">{children}</h2>,
      h3: ({ children }) => <h3 className="text-base font-semibold mt-2 mb-1">{children}</h3>,
      h4: ({ children }) => <h4 className="text-sm font-semibold mt-2 mb-1">{children}</h4>,
      h5: ({ children }) => <h5 className="text-sm font-medium mt-1 mb-1">{children}</h5>,
      h6: ({ children }) => <h6 className="text-sm font-medium mt-1 mb-1">{children}</h6>,
      p: ({ children }) => <p className="my-1">{children}</p>,
      ul: ({ children }) => <ul className="list-disc list-inside space-y-1 my-2">{children}</ul>,
      ol: ({ children }) => <ol className="list-decimal list-inside space-y-1 my-2">{children}</ol>,
      a: ({ href, children }) => {
        const isInternal = href && href.startsWith("/reefcyclopedia/");
        return (
          <a
            href={href}
            onClick={isInternal ? (e) => handleLinkClick(e, href) : undefined}
            target={isInternal ? undefined : "_blank"}
            rel={isInternal ? undefined : "noopener noreferrer"}
            className={isInternal ? "text-blue-600 hover:text-blue-800 hover:underline font-medium" : "text-indigo-600 hover:underline"}
          >
            {children}
          </a>
        );
      },
      code: ({ children }) => (
        <code className="bg-gray-100 px-1 rounded text-sm font-mono">{children}</code>
      ),
      table: ({ children }) => (
        <div className="overflow-x-auto my-3">
          <table className="min-w-full text-sm border border-gray-200 rounded-lg">{children}</table>
        </div>
      ),
      thead: ({ children }) => <thead className="bg-gray-50">{children}</thead>,
      th: ({ children }) => (
        <th className="px-3 py-2 text-left font-semibold text-gray-700 border-b border-gray-200">{children}</th>
      ),
      td: ({ children }) => (
        <td className="px-3 py-2 text-gray-600 border-b border-gray-100">{children}</td>
      ),
      img: ({ src, alt }) => (
        <figure className="my-3">
          <img
            src={src}
            alt={alt || ""}
            onError={fallbackToApiMedia}
            loading="lazy"
            className="rounded-lg w-full object-contain"
            style={{ maxHeight: "400px" }}
          />
          {alt && (
            <figcaption className="text-center text-xs text-gray-500 mt-1 italic">
              {alt}
            </figcaption>
          )}
        </figure>
      ),
    }),
    [handleLinkClick]
  );

  if (!content) return null;

  return (
    <div className={`markdown-content ${className}`}>
      <ReactMarkdown remarkPlugins={REMARK_PLUGINS} components={components}>
        {content}
      </ReactMarkdown>
    </div>
  );
};

export default SimpleMarkdown;
